import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_POLL_MS = 120000;

/**
 * Live dashboard metric cards: load on mount, when refreshDeps change,
 * on window focus, on custom event, and optional polling.
 */
export function useLiveDashboardStats({
  fetcher,
  selectData = (resp) => resp,
  enabled = true,
  refreshDeps = [],
  pollIntervalMs = DEFAULT_POLL_MS,
  refreshOnWindowFocus = true,
  dataChangedEventName = null,
} = {}) {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(Boolean(enabled));
  const fetcherRef = useRef(fetcher);
  const selectRef = useRef(selectData);

  fetcherRef.current = fetcher;
  selectRef.current = selectData;

  const refreshStats = useCallback(async ({ silent = false } = {}) => {
    if (!enabled) return null;
    if (!silent) setLoadingStats(true);
    try {
      const response = await fetcherRef.current();
      if (response == null) return null;
      const next = selectRef.current(response);
      setDashboardStats(next);
      return next;
    } catch (err) {
      if (!silent) {
        console.error("Dashboard stats refresh failed:", err);
      }
      return null;
    } finally {
      if (!silent) setLoadingStats(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoadingStats(false);
      return undefined;
    }
    refreshStats();
    // refreshDeps intentionally drives re-fetch (e.g. reloadToken after save)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, refreshStats, ...refreshDeps]);

  useEffect(() => {
    if (!enabled || !pollIntervalMs || pollIntervalMs < 5000) {
      return undefined;
    }
    const id = window.setInterval(() => {
      refreshStats({ silent: true });
    }, pollIntervalMs);
    return () => window.clearInterval(id);
  }, [enabled, pollIntervalMs, refreshStats]);

  useEffect(() => {
    if (!enabled || !refreshOnWindowFocus) return undefined;
    const onFocus = () => refreshStats({ silent: true });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [enabled, refreshOnWindowFocus, refreshStats]);

  useEffect(() => {
    if (!enabled || !dataChangedEventName) return undefined;
    const onDataChanged = () => refreshStats({ silent: true });
    window.addEventListener(dataChangedEventName, onDataChanged);
    return () => window.removeEventListener(dataChangedEventName, onDataChanged);
  }, [enabled, dataChangedEventName, refreshStats]);

  return { dashboardStats, loadingStats, refreshStats, setDashboardStats };
}
